import useExpireToken from '@/composables/useExpireToken';
import { useToast } from '@/composables/useToast';
import { env } from '@/env';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import FDMessageBox from '@/plugins/fdMessageBox';
import { useUserStore } from '@/stores/user';
import { getToken, tokenExpire } from '@/utils/token';
import { startLoading, stopLoading } from './request-loading';

const { openToast } = useToast();

const handleExpireToken = (): void => {
  if (tokenExpire(getToken())) {
    useUserStore().logout();
    return;
  }
};

const axiosInstance = axios.create({
  /** 基礎路徑 */
  baseURL: env.apiBaseUrl,
  /** 請求超時時間 */
  timeout: 30000
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.headers?.disableGlobalLoading) startLoading();

    if (!config.headers?.unAuthorization) {
      config.headers['Authorization'] = `Bearer ${getToken()}`;
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('Axios instance request error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    stopLoading();
    if (response.status !== 200) {
      // log api 會 201
      if (response.status !== 201) {
        openToast('error', response.data.errorMessage);
      }
    }
    return response;
  },
  (error: AxiosError) => {
    const { config, message, response } = error;
    stopLoading();

    // Token 過期
    if (config && response?.status === 401 && getToken()) {
      console.error('Token expired:', message);
      FDMessageBox.confirm(message, '您重複登入，系統已自動登出', {
        confirmButtonText: '登出',
        type: 'warning',
        showCancelButton: false,
        showClose: false,
        closeOnClickModal: false
      }).then(() => handleExpireToken());
    } else {
      if (config?.headers?.['X-Silent-Error'] === 'true') {
        return Promise.reject(error);
      }
      openToast('error', message);
    }

    console.error('Axios instance response error config:', config);
    return Promise.reject(error);
  }
);

export default axiosInstance;
